import React from "react";
import { Server, ShieldCheck, Laptop, Package, Users, Rocket, CircleCheck, Cpu, ArrowRight } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const competencies = [{
  id: "item-1",
  title: "Infrastrutture IT & Networking",
  tag: "Infrastruttura",
  icon: Server,
  description: "Progettazione, implementazione e gestione di infrastrutture server e reti aziendali ad alta affidabilità. Supportiamo le aziende nell’evoluzione dell’infrastruttura IT garantendo continuità operativa e stabilità dei sistemi.",
  details: ["Server & System Environments", "Virtualization Platforms", "Enterprise Network Infrastructure", "Hybrid Cloud Architectures", "Infrastructure Monitoring Systems", "Enterprise Storage Systems"]
}, {
  id: "item-2",
  title: "Cybersecurity & Data Protection",
  tag: "Sicurezza",
  icon: ShieldCheck,
  description: "Strategie di protezione per salvaguardare gli asset digitali aziendali. Dalla sicurezza degli endpoint alla gestione degli accessi e delle strategie di backup per garantire continuità operativa e protezione dei dati.",
  details: ["Endpoint Protection", "Identity & Access Management (IAM)", "Multi-Factor Authentication (MFA)", "Backup & Disaster Recovery", "Secure Remote Access", "Device & Endpoint Management"]
}, {
  id: "item-3",
  title: "Gestione Dispositivi & Endpoint",
  tag: "Hardware",
  icon: Laptop,
  description: "Gestione centralizzata del ciclo di vita dei dispositivi aziendali per garantire sicurezza, efficienza operativa e controllo degli endpoint. Dalla configurazione standardizzata dei sistemi alla gestione degli aggiornamenti e al supporto tecnico avanzato.",
  details: ["Mobile Device Management (MDM)", "Gestione Flotte Laptop & Workstation", "VoIP & Telefonia Cloud Aziendale", "Asset Tracking & Inventory", "Configurazione Standardizzata (Golden Image)", "Supporto Helpdesk"]
}, {
  id: "item-4",
  title: "Tecnologie per la Logistica",
  tag: "Operativo",
  icon: Package,
  description: "Supporto specializzato per le tecnologie operative utilizzate in ambienti logistici e industriali. Integrazione e gestione di dispositivi rugged e sistemi di tracciamento nei flussi operativi di magazzino e produzione.",
  details: ["RF Scanner & Dispositivi Rugged per la Logistica", "Stampanti Termiche & Etichettatrici", "Reti Wi-Fi per Ambienti Logistici e Industriali", "Provisioning e Staging dei Dispositivi", "Radio Digitali & Comunicazione Operativa", "Manutenzione Hardware e Supporto On-Site"]
}, {
  id: "item-5",
  title: "Collaboration & Meeting Rooms",
  tag: "Comunicazione",
  icon: Users,
  description: "Soluzioni tecnologiche per la collaborazione aziendale e la comunicazione operativa. Progettazione e integrazione di sistemi per sale riunioni, piattaforme di videoconferenza e infrastrutture di comunicazione per team distribuiti",
  details: ["Microsoft Teams Rooms & Zoom Rooms", "Sistemi di Videoconferenza Hardware", "VoIP & Telefonia Cloud Aziendale", "Integrazione Audio/Video per Sale Riunioni", "Infrastrutture per Collaboration & UC", "Sistemi di Comunicazione per Ambienti Aziendali"]
}, {
  id: "item-6",
  title: "Deployment & Rollout Tecnologici",
  tag: "Progetti",
  icon: Rocket,
  description: "Pianificazione e gestione di progetti di deployment tecnologico su larga scala. Coordinamento delle attività di rollout, configurazione massiva dei dispositivi e installazioni on-site per nuove sedi operative o aggiornamento delle postazioni utente.",
  details: ["Project Management", "Imaging & Provisioning Massivo", "Logistica e Distribuzione Hardware", "Migrazione Dati Utente", "Rollout Tecnologico Multi-Sede", "Installazione e Configurazione On-Site"]
}];
export default function CatalogoCompetenze() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="catalogo-competenze" className="dark bg-background text-foreground py-20 relative overflow-hidden">
      {}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-primary/20 to-transparent" />
        <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-primary/20 to-transparent" />
        <div className="absolute top-20 right-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-20 left-0 w-64 h-64 bg-secondary/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div ref={ref as React.RefObject<HTMLDivElement>} className={`max-w-4xl mx-auto transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="mb-12 text-center">
            <Badge variant="outline" className="mb-4 border-primary/30 text-primary uppercase tracking-wider">
              Competenze Tecniche
            </Badge>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4 text-white">
              Catalogo Servizi & Tecnologie
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Un approccio strutturato alla gestione IT. Esplora le aree di competenza e le tecnologie supportate per garantire efficienza e sicurezza alla tua azienda.
            </p>
          </div>

          <div className="bg-card/30 backdrop-blur-sm border border-white/5 rounded-xl overflow-hidden shadow-2xl">
            <Accordion type="single" collapsible className="w-full">
              {competencies.map((item, i) => <AccordionItem key={item.id} value={item.id} className="border-b border-white/5 last:border-0 px-6 transition-colors hover:bg-white/5" data-index={i}>
                  <AccordionTrigger className="hover:no-underline py-6 group">
                    <div className="flex items-center gap-4 w-full text-left">
                      <div className="p-2 rounded-lg bg-secondary/30 text-primary group-hover:bg-primary/20 group-hover:text-primary-foreground transition-colors">
                        <item.icon className="size-5 md:size-6" />
                      </div>
                      <div className="flex-1 flex flex-col md:flex-row md:items-center justify-between gap-2">
                        <span className="text-lg font-semibold text-foreground group-hover:text-primary transition-colors">
                          {item.title}
                        </span>
                        <Badge variant="secondary" className="w-fit bg-secondary/50 text-secondary-foreground/80 border-0">
                          {item.tag}
                        </Badge>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pb-6 pt-2">
                    <div className="pl-[3.25rem]">
                      <p className="text-muted-foreground mb-6 text-base leading-relaxed border-l-2 border-primary/20 pl-4">
                        {item.description}
                      </p>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {item.details.map((detail, idx) => <div key={idx} className="flex items-center gap-2 text-sm text-foreground/80 bg-background/40 p-2 rounded border border-white/5" data-index={idx}>
                            <Cpu className="size-3.5 text-primary shrink-0" />
                            <span>{detail}</span>
                          </div>)}
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>

          <div className="mt-12 text-center">
            <div className="inline-flex flex-col items-center gap-4 p-6 rounded-lg bg-secondary/10 border border-white/5 backdrop-blur-sm">
              <p className="text-foreground font-medium">
                Hai bisogno di una soluzione personalizzata per la tua infrastruttura?
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_0_20px_-5px_var(--color-primary)]">
                  <Link to="/ContactsPage">
                    Richiedi una consulenza tecnica
                    <ArrowRight className="ml-2 size-4" />
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="border-white/10 hover:bg-white/5 text-foreground">
                  <Link to="/ExperiencePage">
                    Scopri il metodo di lavoro
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}